/**
 * Gestionnaire de benchmarks pour mesurer l'impact des optimisations du cache
 */

export class BenchmarkManager {
    constructor() {
        this.benchmarks = [];
        this.currentBenchmark = null;
    }

    /**
     * Démarre un nouveau benchmark
     * @param {string} name - Nom du benchmark
     * @param {string} description - Description des optimisations
     */
    startBenchmark(name, description = '') {
        if (typeof window === 'undefined' || !window.gedcomDataStore) {
            console.error('❌ GedcomDataStore non disponible');
            return null;
        }

        const cache = window.gedcomDataStore.getIndividualsCache();
        const individuals = Array.from(cache.values());
        
        if (individuals.length === 0) {
            console.error('❌ Cache vide - importez d\'abord un fichier GEDCOM');
            return null;
        }

        console.log(`📊 Démarrage du benchmark: ${name}`);
        
        this.currentBenchmark = {
            name,
            description,
            timestamp: new Date().toISOString(),
            totalIndividuals: individuals.length,
            ...this.analyzeCacheStructure(individuals)
        };

        return this.currentBenchmark;
    }

    /**
     * Analyse la structure détaillée du cache
     */
    analyzeCacheStructure(individuals) {
        const sample = individuals[0];
        const totalSize = JSON.stringify(individuals).length;
        const avgSize = Math.round(totalSize / individuals.length);

        // Analyser la présence des champs
        const fieldPresence = {
            formatted: {
                formattedBirth: 0,
                formattedDeath: 0,
                formattedOccupations: 0,
                formattedMarriages: 0,
                formattedSiblings: 0
            },
            calculated: {
                personLink: 0,
                age: 0,
                birthYear: 0,
                deathYear: 0
            },
            empty: {
                emptyStrings: 0,
                emptyArrays: 0,
                nullValues: 0
            },
            stats: {
                present: 0,
                totalSize: 0
            }
        };

        // Calculer les tailles par catégorie
        const categorySizes = {
            formatted: 0,
            calculated: 0,
            events: 0,
            stats: 0,
            core: 0
        };

        individuals.forEach(person => {
            // Champs formatted
            Object.keys(fieldPresence.formatted).forEach(field => {
                if (field in person) {
                    fieldPresence.formatted[field]++;
                    categorySizes.formatted += JSON.stringify(person[field] || '').length;
                }
            });

            // Champs calculés
            Object.keys(fieldPresence.calculated).forEach(field => {
                if (field in person) {
                    fieldPresence.calculated[field]++;
                    categorySizes.calculated += JSON.stringify(person[field] || '').length;
                }
            });

            // Événements
            if (person.individualEvents) {
                categorySizes.events += JSON.stringify(person.individualEvents).length;
            }

            // Stats
            if (person.stats) {
                fieldPresence.stats.present++;
                const statsSize = JSON.stringify(person.stats).length;
                fieldPresence.stats.totalSize += statsSize;
                categorySizes.stats += statsSize;
            }

            // Valeurs vides
            Object.entries(person).forEach(([key, value]) => {
                if (value === '') fieldPresence.empty.emptyStrings++;
                else if (Array.isArray(value) && value.length === 0) fieldPresence.empty.emptyArrays++;
                else if (value === null) fieldPresence.empty.nullValues++;
            });
        });

        // Taille des données core (tout sauf formatted, calculated, events, stats)
        categorySizes.core = totalSize - categorySizes.formatted - categorySizes.calculated - 
                            categorySizes.events - categorySizes.stats;

        return {
            totalSize,
            avgSize,
            fieldPresence,
            categorySizes,
            structure: Object.keys(sample).sort()
        };
    }

    /**
     * Termine le benchmark actuel et l'enregistre
     */
    finishBenchmark() {
        if (!this.currentBenchmark) {
            console.error('❌ Aucun benchmark en cours');
            return null;
        }

        const finished = { ...this.currentBenchmark };
        this.benchmarks.push(finished);
        this.currentBenchmark = null;

        console.log(`✅ Benchmark "${finished.name}" terminé`);
        this.displayBenchmarkResults(finished);

        return finished;
    }

    /**
     * Affiche les résultats d'un benchmark
     */
    displayBenchmarkResults(benchmark) {
        console.group(`📋 Résultats du benchmark: ${benchmark.name}`);
        
        console.log(`📊 Taille totale: ${(benchmark.totalSize / 1024 / 1024).toFixed(2)} MB`);
        console.log(`👥 Individus: ${benchmark.totalIndividuals}`);
        console.log(`📏 Taille moyenne: ${benchmark.avgSize} bytes`);
        
        console.log('\n🗂️ Répartition par catégorie:');
        Object.entries(benchmark.categorySizes).forEach(([category, size]) => {
            const percent = ((size / benchmark.totalSize) * 100).toFixed(1);
            const sizeKB = (size / 1024).toFixed(1);
            console.log(`  ${category}: ${sizeKB} KB (${percent}%)`);
        });

        console.log('\n🔍 Présence des champs:');
        console.log('  Formatted:', Object.entries(benchmark.fieldPresence.formatted)
            .filter(([, count]) => count > 0)
            .map(([field, count]) => `${field}: ${count}`)
            .join(', ') || 'Aucun');
        
        console.log('  Calculated:', Object.entries(benchmark.fieldPresence.calculated)
            .filter(([, count]) => count > 0)
            .map(([field, count]) => `${field}: ${count}`)
            .join(', ') || 'Aucun');

        console.log(`  Stats: ${benchmark.fieldPresence.stats.present}/${benchmark.totalIndividuals} (${(benchmark.fieldPresence.stats.totalSize / 1024).toFixed(1)} KB)`);

        console.groupEnd();
    }

    /**
     * Compare deux benchmarks
     */
    compareBenchmarks(benchmark1Name, benchmark2Name) {
        const b1 = this.benchmarks.find(b => b.name === benchmark1Name);
        const b2 = this.benchmarks.find(b => b.name === benchmark2Name);

        if (!b1 || !b2) {
            console.error('❌ Benchmarks non trouvés');
            return null;
        }

        const sizeDiff = b1.totalSize - b2.totalSize;
        const percentDiff = ((sizeDiff / b1.totalSize) * 100).toFixed(1);

        console.group(`🔄 Comparaison: ${b1.name} → ${b2.name}`);
        console.log(`📊 Taille: ${(b1.totalSize / 1024 / 1024).toFixed(2)} MB → ${(b2.totalSize / 1024 / 1024).toFixed(2)} MB`);
        console.log(`💾 Économie: ${(sizeDiff / 1024).toFixed(1)} KB (${percentDiff}%)`);
        
        console.log('\n📈 Évolution par catégorie:');
        Object.keys(b1.categorySizes).forEach(category => {
            const diff = b1.categorySizes[category] - b2.categorySizes[category];
            if (Math.abs(diff) > 1000) { // Afficher seulement les différences significatives
                const diffKB = (diff / 1024).toFixed(1);
                const sign = diff > 0 ? '-' : '+';
                console.log(`  ${category}: ${sign}${Math.abs(diffKB)} KB`);
            }
        });

        console.groupEnd();

        return { b1, b2, sizeDiff, percentDiff };
    }

    /**
     * Exporte tous les benchmarks
     */
    exportBenchmarks() {
        const data = {
            exportDate: new Date().toISOString(),
            benchmarks: this.benchmarks
        };

        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = `benchmarks-${new Date().toISOString().slice(0, 16).replace(/[T:]/g, '-')}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        console.log('📁 Benchmarks exportés');
        return data;
    }

    /**
     * Affiche un résumé de tous les benchmarks
     */
    showSummary() {
        if (this.benchmarks.length === 0) {
            console.log('❌ Aucun benchmark enregistré');
            return;
        }

        console.group('📊 Résumé de tous les benchmarks');
        
        this.benchmarks.forEach((benchmark, index) => {
            const sizeMB = (benchmark.totalSize / 1024 / 1024).toFixed(2);
            console.log(`${index + 1}. ${benchmark.name}: ${sizeMB} MB (${benchmark.description})`);
        });

        if (this.benchmarks.length > 1) {
            const first = this.benchmarks[0];
            const last = this.benchmarks[this.benchmarks.length - 1];
            const totalSavings = first.totalSize - last.totalSize;
            const totalPercent = ((totalSavings / first.totalSize) * 100).toFixed(1);
            
            console.log(`\n🎯 Économie totale: ${(totalSavings / 1024).toFixed(1)} KB (${totalPercent}%)`);
        }

        console.groupEnd();
    }

    /**
     * Benchmark rapide de l'état actuel
     */
    quickBenchmark(name = `Quick-${Date.now()}`) {
        this.startBenchmark(name);
        return this.finishBenchmark();
    }
}

// Instance globale
const benchmarkManager = new BenchmarkManager();

// Rendre disponible globalement
if (typeof window !== 'undefined') {
    window.benchmarkManager = benchmarkManager;
}

export default benchmarkManager;